import React, { useState } from "react";
import { motion } from "framer-motion";
import { Modal, Button, Text } from "@mantine/core";
import "./reset-password.css";

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [formTouched, setFormTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const [modalOpened, setModalOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/user/change-password";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFormTouched((prev) => ({ ...prev, [name]: true }));
    validateForm();
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const touched = formTouched;

    if (touched.currentPassword && !formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }
    if (touched.newPassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = "New password is required";
      } else if (!/^(?=.*[A-Z])(?=.*[\W_]).{8,}$/.test(formData.newPassword)) {
        newErrors.newPassword =
          "Password must contain at least 8 characters, one uppercase letter, and one symbol";
      }
    }
    if (touched.confirmNewPassword) {
      if (!formData.confirmNewPassword) {
        newErrors.confirmNewPassword = "Please confirm your new password";
      } else if (formData.confirmNewPassword !== formData.newPassword) {
        newErrors.confirmNewPassword = "Passwords do not match";
      }
    }

    setFormErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const isFormIncomplete = () => {
    return Object.values(formData).some((value) => value === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormTouched({
      currentPassword: true,
      newPassword: true,
      confirmNewPassword: true,
    });

    if (!validateForm()) return;

    setIsLoading(true);
    setApiError(null);

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try logging in again.");
      }

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          confirm_new_password: formData.confirmNewPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        switch (response.status) {
          case 400:
            throw new Error(data.error || "Invalid input data.");
          case 401:
            throw new Error("You must be logged in to change your password.");
          case 403:
            throw new Error("Current password is incorrect.");
          default:
            throw new Error(data.message || "Failed to reset password. Please try again.");
        }
      }

      setModalOpened(true);
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setFormTouched({
        currentPassword: false,
        newPassword: false,
        confirmNewPassword: false,
      });
      setShowPasswords({
        currentPassword: false,
        newPassword: false,
        confirmNewPassword: false,
      });
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setModalOpened(false);
    // Force page refresh to sync with new authentication cookie if modal is closed without navigating
    window.location.reload();
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <div className="reset-password-container">
      <motion.div
        className="reset-password-content"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 variants={itemVariants}>Reset password</motion.h2>
        <motion.div className="reset-password-card" variants={itemVariants}>
          <motion.form
            onSubmit={handleSubmit}
            className="reset-password-form"
            variants={formVariants}
          >
            <motion.div variants={itemVariants} className="form-group">
              <label>Current password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.currentPassword ? "text" : "password"}
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your password"
                />
                <span
                  className="toggle-password"
                  onClick={() => togglePasswordVisibility("currentPassword")}
                >
                  {showPasswords.currentPassword ? "Hide" : "Show"}
                </span>
              </div>
              {formErrors.currentPassword && (
                <span className="error">{formErrors.currentPassword}</span>
              )}
            </motion.div>
            <motion.div variants={itemVariants} className="form-group">
              <label>New password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.newPassword ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your password"
                />
                <span
                  className="toggle-password"
                  onClick={() => togglePasswordVisibility("newPassword")}
                >
                  {showPasswords.newPassword ? "Hide" : "Show"}
                </span>
              </div>
              {formErrors.newPassword && (
                <span className="error">{formErrors.newPassword}</span>
              )}
            </motion.div>
            <motion.div variants={itemVariants} className="form-group">
              <label>Confirm new password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.confirmNewPassword ? "text" : "password"}
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Re-enter your password"
                />
                <span
                  className="toggle-password"
                  onClick={() => togglePasswordVisibility("confirmNewPassword")}
                >
                  {showPasswords.confirmNewPassword ? "Hide" : "Show"}
                </span>
              </div>
              {formErrors.confirmNewPassword && (
                <span className="error">{formErrors.confirmNewPassword}</span>
              )}
            </motion.div>
          </motion.form>
        </motion.div>
        {apiError && (
          <motion.p className="error-message" variants={itemVariants}>
            {apiError}
          </motion.p>
        )}
        <motion.div variants={itemVariants} className="button-group">
          <motion.button
            type="button"
            onClick={handleSubmit}
            className={`reset-button ${
              isLoading ||
              isFormIncomplete() ||
              Object.values(formErrors).some((error) => error)
                ? "disabled"
                : ""
            }`}
            disabled={
              isLoading ||
              isFormIncomplete() ||
              Object.values(formErrors).some((error) => error)
            }
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            {isLoading ? "Resetting password" : "Reset password"}
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Success Modal */}
      <Modal
        opened={modalOpened}
        onClose={handleModalClose}
        closeOnEscape={false}
        closeOnClickOutside={false}
        withCloseButton={false}
        centered
        size="sm"
        className="success-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <div className="modal-content">
          <div className="modal-message-box">
            <Text className="modal-title">Password reset successful</Text>
            <Text className="modal-message">
              Your password has been successfully reset. You can now log in using your new password.
            </Text>
            <Button
              component="a"
              href="/dashboard"
              className="modal-close-button"
              style={{ height: "fit-content" }}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ResetPassword;